import { useEffect, useMemo, useState } from "react";
import { Form, useActionData, useNavigation } from "@remix-run/react";
import { useCart } from "~/context/cartContext";
import { Minus, Plus, ShoppingCart, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { Textarea } from "~/components/ui/textarea";
import { Badge } from "~/components/ui/badge";
import { formatPrice, sellingPrice } from "~/lib/helpers/storehelpers";
import { ProductCard } from "~/components/ProductCard";
import { Product, Store } from "~/lib/types";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import ButtonTheme from "./theme/Button";
import HorizontalScroller from "./HorizontalScroller";
interface Review {
  id: string;
  rating: number;
  title?: string | null;
  comment: string | null;
  created_at: string;
  user_id: string;
  reviewerName: string;
  reviewerInitials: string;
}

interface ProductDetailProps {
  product: Product;
  store: Store;
  reviews: Review[];
  recommendations: Array<Product & { mainImageUrl?: string }>;
  imageUrls: string[];
  averageRating: number;
  actionData?: any;
  userId?: string | null;
}

export function ProductDetail({
  product,
  store,
  reviews,
  recommendations,
  imageUrls,
  averageRating,
  actionData,
  userId,
}: ProductDetailProps) {
  const navigation = useNavigation();
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [reviewAuthor, setReviewAuthor] = useState("");
  const [reviewEmail, setReviewEmail] = useState("");
  const { addToCart, canAddStoreProduct } = useCart();
  const storeIdForCart = product.store_id || store.id;

  const minQuantity = Math.max(1, product.minimum_order ?? 1);
  const maxQuantity = product.maximum_order;
  const [quantity, setQuantity] = useState(minQuantity);

  const effectiveUnitPrice = useMemo(() => {
    const sale = product.markdown;
    const list = product.price;
    if (sale != null) return Number(sale);
    if (list != null) return Number(list);
    return 0;
  }, [product.markdown, product.price]);

  useEffect(() => {
    const min = Math.max(1, product.minimum_order ?? 1);
    setQuantity(min);
  }, [product.id, product.minimum_order]);

  const getUnitLabel = (plural = false) => {
    const base = String(product.per_unit ?? "unit").toLowerCase();
    if (plural) {
      return base.endsWith("s") ? base : `${base}s`;
    }
    return base;
  };

  const clampQuantity = (value: number) => {
    const min = Math.max(1, product.minimum_order ?? 1);
    let next = value;
    if (Number.isNaN(next) || next < min) next = min;
    if (maxQuantity != null && next > maxQuantity) next = maxQuantity;
    return next;
  };

  const handleQuantityDelta = (delta: number) => {
    setQuantity((q) => clampQuantity(q + delta));
  };

  const displaySelling = sellingPrice(product.price, product.markdown);
  const totalPriceFormatted = formatPrice(effectiveUnitPrice * quantity);

  const cartLineUnitPrice = displaySelling != null && displaySelling > 0
    ? displaySelling
    : 0;
  const canAddToCart = Boolean(addToCart) &&
    (displaySelling == null || displaySelling >= 0) &&
    canAddStoreProduct(storeIdForCart);

  // Handle successful review submission
  useEffect(() => {
    if (actionData && "success" in actionData) {
      setReviewRating(5);
      setReviewTitle("");
      setReviewComment("");
      setReviewAuthor("");
      setReviewEmail("");
      // Refresh the page to show the new review
      window.location.reload();
    }
  }, [actionData]);

  // Scroll to top on initial navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-800">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto py-12">
        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-lg overflow-hidden bg-white">
              <img
                src={imageUrls[selectedImage] || "/images/placeholder.svg"}
                alt={product.name || "Product"}
                className="h-full w-full object-cover"
              />
            </div>
            {imageUrls.length > 1 && (
              <div className="flex gap-2">
                {imageUrls.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex items-center justify-center p-1 bg-white ${
                      selectedImage === index
                        ? "border-blue-600"
                        : "border-gray-200"
                    }`}
                  >
                    <img
                      src={image || "/images/placeholder.svg"}
                      alt={`${product.name || "Product"} ${index + 1}`}
                      className="h-full w-full object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">
                  {product.name}
                </h1>
              </div>

              {/* Price */}
              <div className="mb-4">
                {displaySelling === null && (
                  <p className="text-gray-600 text-sm font-medium mt-1">
                    Contact Supplier for Pricing
                  </p>
                )}
                {displaySelling === 0 && (
                  <p className="mt-1 text-base font-normal text-gray-500">
                    Contact Supplier for Pricing
                  </p>
                )}
                {displaySelling !== null &&
                  displaySelling !== 0 &&
                  (() => {
                    const md = product.markdown;
                    const pr = product.price;
                    const showSale = md != null &&
                      pr != null &&
                      md > 0 &&
                      pr > md;
                    if (showSale) {
                      return (
                        <div className="mt-1">
                          <span className="text-green-600 text-sm font-medium">
                            ${formatPrice(md)}
                          </span>
                          <span className="text-gray-400 text-xs ml-2 line-through">
                            ${formatPrice(pr)}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <p className="text-gray-900 dark:text-gray-100 font-bold text-2xl mt-1">
                        ${formatPrice(displaySelling)}
                      </p>
                    );
                  })()}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-5 w-5 ${
                        star <= Math.round(averageRating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-lg font-medium">
                  {averageRating.toFixed(1)}/5
                </span>
                {reviews.length > 0 && (
                  <span className="text-gray-500">
                    ({reviews.length}{" "}
                    {reviews.length === 1 ? "review" : "reviews"})
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {product.description || "No description available"}
              </p>

              {/* Product Details */}
              {(product.per_unit || product.minimum_order ||
                product.maximum_order ||
                (product.categories && product.categories.length > 0)) && (
                <div className="mb-6 space-y-2">
                  {product.per_unit && (
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      <span className="font-medium">Unit:</span>{" "}
                      <Badge
                        variant="outline"
                        key={product.per_unit?.charAt(0) +
                          product.per_unit?.slice(1).toLowerCase()}
                      >
                        {product.per_unit?.charAt(0) +
                          product.per_unit?.slice(1).toLowerCase()}
                      </Badge>
                    </p>
                  )}
                  {product.minimum_order && (
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      <span className="font-medium">Minimum order:</span>{" "}
                      {product.minimum_order}
                    </p>
                  )}
                  {product.maximum_order && (
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      <span className="font-medium">Maximum order:</span>{" "}
                      {product.maximum_order}
                    </p>
                  )}
                  {
                    /* {product.categories && product.categories.length > 0 && (
                    <div className="mt-3">
                      <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">Categories:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.categories.map((categoryName) => (
                          <Badge
                            key={categoryName}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {categoryName}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )} */
                  }
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="quantity">Quantity</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <ButtonTheme
                      mode="secondary"
                      size="sm"
                      type="button"
                      onClick={() => handleQuantityDelta(-1)}
                      disabled={quantity <= minQuantity}
                    >
                      <Minus className="h-4 w-4" />
                    </ButtonTheme>
                    <Input
                      id="quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const parsed = Number.parseInt(e.target.value, 10);
                        setQuantity(
                          clampQuantity(
                            Number.isNaN(parsed) ? minQuantity : parsed,
                          ),
                        );
                      }}
                      className="w-24 text-center"
                      min={minQuantity}
                      max={maxQuantity ?? undefined}
                    />
                    <ButtonTheme
                      mode="secondary"
                      size="sm"
                      type="button"
                      onClick={() => handleQuantityDelta(1)}
                      disabled={maxQuantity != null && quantity >= maxQuantity}
                    >
                      <Plus className="h-4 w-4" />
                    </ButtonTheme>
                    <span className="text-sm text-muted-foreground">
                      {getUnitLabel(true)}
                    </span>
                  </div>
                </div>

                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span>Unit Price:</span>
                    <span>
                      {displaySelling === null && (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {displaySelling === 0 && (
                        <span className="text-sm font-normal text-muted-foreground">
                          Contact Supplier for Pricing
                        </span>
                      )}
                      {displaySelling !== null &&
                        displaySelling !== 0 && (
                        <>
                          ${formatPrice(effectiveUnitPrice)}/{getUnitLabel()}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Quantity:</span>
                    <span>
                      {quantity} {getUnitLabel(true)}
                    </span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between items-center font-bold text-lg">
                    <span>Total:</span>
                    <span>
                      {displaySelling === null && (
                        <span className="text-muted-foreground font-normal text-base">
                          —
                        </span>
                      )}
                      {displaySelling === 0 && (
                        <span className="text-base font-normal text-muted-foreground">
                          Contact Supplier for Pricing
                        </span>
                      )}
                      {displaySelling !== null &&
                        displaySelling !== 0 && <>${totalPriceFormatted}</>}
                    </span>
                  </div>
                </div>

                <ButtonTheme
                  mode="primary"
                  size="lg"
                  type="button"
                  className="w-full flex items-center justify-center gap-2"
                  disabled={!canAddToCart}
                  onClick={() => {
                    if (!canAddToCart) return;
                    addToCart({
                      id: Date.now().toString(),
                      product_id: product.id,
                      name: product.name || "Unnamed Product",
                      price: cartLineUnitPrice,
                      image: imageUrls[0] || "",
                      quantity,
                      store_id: product.store_id || store.id,
                      store_name: store.name,
                    });
                  }}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Add to Cart
                </ButtonTheme>
              </div>
            </div>
          </div>
        </div>

        {/* You May Also Like Section */}
        {recommendations.length > 0 && (
          <div className="mb-12">
            <h3 className="text-2xl font-semibold mb-6">You May Also Like</h3>
            <HorizontalScroller>
              {recommendations.map((recProduct) => {
                // Extract mainImageUrl from the pre-processed recommendation
                const { mainImageUrl, ...productDetails } = recProduct;
                return (
                  <ProductCard
                    key={recProduct.id}
                    productId={recProduct.id}
                    productDetails={productDetails}
                    storeName={store.name}
                    size="promo"
                  />
                );
              })}
            </HorizontalScroller>
          </div>
        )}

        {/* Product Reviews Section */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold mb-6">
            Product Reviews
            {reviews.length > 0 && (
              <span className="text-lg font-normal text-gray-600 ml-2">
                {averageRating.toFixed(2)} ({reviews.length}{" "}
                {reviews.length === 1 ? "review" : "reviews"})
              </span>
            )}
          </h3>

          {/* Review Form */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Write a Review</CardTitle>
            </CardHeader>
            <CardContent>
              <Form method="post" className="space-y-4">
                <input type="hidden" name="action" value="addReview" />

                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    placeholder="Enter a title for your review"
                    value={reviewTitle}
                    onChange={(e) => setReviewTitle(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="review">Review</Label>
                  <Textarea
                    id="review"
                    name="comment"
                    placeholder="Share your thoughts and experience"
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    rows={4}
                    className="focus-visible:border-orange-300 focus-visible:ring-2 focus-visible:ring-orange-500/25 focus-visible:ring-offset-0"
                  />
                </div>

                <div>
                  <Label>Rating</Label>
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewRating(star)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`h-6 w-6 ${
                            star <= reviewRating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="rating" value={reviewRating} />
                </div>

                {
                  /* <div>
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    name="author"
                    placeholder="Enter your name"
                    value={reviewAuthor}
                    onChange={(e) => setReviewAuthor(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={reviewEmail}
                    onChange={(e) => setReviewEmail(e.target.value)}
                  />
                </div> */
                }

                <ButtonTheme
                  type="submit"
                  disabled={!userId || isSubmitting}
                  className="w-full"
                >
                  {!userId
                    ? "Sign in to review"
                    : isSubmitting
                    ? "Submitting..."
                    : "Submit Review"}
                </ButtonTheme>

                {actionData && "error" in actionData && (
                  <p className="text-red-500 text-sm">{actionData.error}</p>
                )}
              </Form>
            </CardContent>
          </Card>

          {/* Existing Reviews */}
          {reviews.length > 0
            ? (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card
                    key={review.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <Avatar className="h-10 w-10 border">
                          <AvatarFallback>
                            {review.reviewerInitials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-sm">
                                {review.reviewerName}
                              </h4>
                              <time className="text-xs text-gray-500">
                                {formatDistanceToNow(
                                  new Date(review.created_at),
                                  {
                                    addSuffix: true,
                                  },
                                )}
                              </time>
                            </div>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-4 w-4 ${
                                    star <= review.rating
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          {review.title && (
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {review.title}
                            </p>
                          )}
                          {review.comment && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {review.comment}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
            : (
              <p className="text-gray-500 text-center py-8">
                No reviews yet. Be the first to review this product!
              </p>
            )}
        </div>
      </div>
    </div>
  );
}
